/**
 * Deterministic 2D value noise + fractal Brownian motion for terrain and
 * moisture fields. Pure and seedable — no dependence on Math.random. Value
 * noise (not simplex) keeps this short and dependency-free; for rolling hills
 * it's indistinguishable.
 */

function hash2(ix: number, iy: number, seed: number): number {
  // Integer hash → [0, 1). Cheap, well-mixed enough for terrain.
  let h = (ix | 0) * 374761393 + (iy | 0) * 668265263 + (seed | 0) * 2147483647;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, y: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = smoothstep(x - x0);
  const fy = smoothstep(y - y0);

  const v00 = hash2(x0, y0, seed);
  const v10 = hash2(x0 + 1, y0, seed);
  const v01 = hash2(x0, y0 + 1, seed);
  const v11 = hash2(x0 + 1, y0 + 1, seed);

  const a = v00 + (v10 - v00) * fx;
  const b = v01 + (v11 - v01) * fx;
  return a + (b - a) * fy; // [0, 1]
}

/** Fractal noise in [0, 1]. `freq` cells per unit; `octaves` adds detail. */
export function fbm(
  x: number,
  y: number,
  seed: number,
  octaves: number,
  freq: number,
  lacunarity = 2.0,
  gain = 0.5,
): number {
  let amp = 1;
  let f = freq;
  let sum = 0;
  let norm = 0;
  for (let o = 0; o < octaves; o++) {
    sum += amp * valueNoise(x * f, y * f, seed + o * 1013);
    norm += amp;
    amp *= gain;
    f *= lacunarity;
  }
  return sum / norm;
}
